import React, { useState, useMemo } from 'react';
import { CheckCircle, AlertCircle, RefreshCw, FileText, TrendingUp, ArrowRight, Download, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from 'sonner';
import { BankStatement, BankTransaction } from '../../types/bank-reconciliation';
import { BankStatementProcessor } from '../../services/reconciliation/BankStatementProcessor';
import { useAuth } from '@/hooks/useAuth';

interface TransactionSummaryProps {
  statement: BankStatement;
  transactions: BankTransaction[];
  onAutoMatch?: () => void;
  onNewImport?: () => void;
  isProcessing?: boolean;
}

interface TransactionStats {
  total: number;
  matched: number;
  unmatched: number;
  pending: number;
  totalCredit: number;
  totalDebit: number;
  matchRate: number;
}

export function TransactionSummary({
  statement,
  transactions,
  onAutoMatch,
  onNewImport,
  isProcessing = false
}: TransactionSummaryProps) {
  const { user } = useAuth();
  const [selectedTransaction, setSelectedTransaction] = useState<BankTransaction | null>(null);

  // Calculate statistics
  const stats = useMemo<TransactionStats>(() => {
    const total = transactions.length;
    const matched = transactions.filter(t => t.match_status === 'matched').length;
    const unmatched = transactions.filter(t => t.match_status === 'unmatched').length;
    const pending = transactions.filter(t => t.match_status === 'pending').length;

    const totalCredit = transactions
      .filter(t => t.amount > 0)
      .reduce((sum, t) => sum + t.amount, 0);

    const totalDebit = Math.abs(transactions
      .filter(t => t.amount < 0)
      .reduce((sum, t) => sum + t.amount, 0));

    const matchRate = total > 0 ? (matched / total) * 100 : 0;

    return {
      total,
      matched,
      unmatched,
      pending,
      totalCredit,
      totalDebit,
      matchRate
    };
  }, [transactions]);

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount);
  };

  // Format date
  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'Data não disponível';

    try {
      const dateObj = date instanceof Date ? date : new Date(date);
      // Check if the date is valid
      if (isNaN(dateObj.getTime())) {
        return 'Data inválida';
      }

      return new Intl.DateTimeFormat('pt-BR').format(dateObj);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Data inválida';
    }
  };

  // Handle auto-match
  const handleAutoMatch = async () => {
    if (!user || !statement) return;

    try {
      toast.loading('Executando conciliação automática...');

      const result = await BankStatementProcessor.autoMatch(statement.id, user.id);

      toast.success(`Conciliação concluída! ${result.matched} transações pareadas.`);

      // Trigger parent callback to refresh data
      if (onAutoMatch) {
        onAutoMatch();
      }

    } catch (error) {
      console.error('Error in auto-match:', error);
      toast.error('Erro na conciliação automática');
    } finally {
      toast.dismiss();
    }
  };

  // Get bank info
  const getBankName = (bankCode: string) => {
    const bankMap: { [key: string]: string } = {
      '001': 'Banco do Brasil',
      '033': 'Santander',
      '104': 'Caixa',
      '237': 'Bradesco',
      '341': 'Itaú',
      '260': 'Nubank',
      '077': 'Inter',
      '756': 'Sicoob',
      '748': 'Sicredi',
      '208': 'BTG Pactual'
    };
    return bankMap[bankCode] || bankCode;
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'matched':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'unmatched':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-card text-gray-800 border-border';
    }
  };

  // Get status text
  const getStatusText = (status: string) => {
    switch (status) {
      case 'matched':
        return 'Pareado';
      case 'unmatched':
        return 'Não Pareado';
      case 'pending':
        return 'Pendente';
      default:
        return status;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Resumo da Conciliação</h2>
          <p className="text-muted-foreground">
            Extrato de {getBankName(statement.bank_code)} - {formatDate(statement.start_date)} a {formatDate(statement.end_date)}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={onNewImport}
            disabled={isProcessing}
          >
            <FileText className="h-4 w-4 mr-2" />
            Nova Importação
          </Button>

          <Button
            onClick={handleAutoMatch}
            disabled={isProcessing || stats.matched === stats.total}
          >
            {isProcessing ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Processando...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Conciliar Automaticamente
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total de Transações</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <FileText className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pareadas</p>
                <p className="text-2xl font-bold text-green-600">{stats.matched}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Não Pareadas</p>
                <p className="text-2xl font-bold text-red-600">{stats.unmatched}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Taxa de Pareamento</p>
                <p className="text-2xl font-bold">{stats.matchRate.toFixed(1)}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress Bar */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Progresso da Conciliação</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span>{stats.matched} de {stats.total} transações pareadas</span>
              <span>{stats.matchRate.toFixed(1)}%</span>
            </div>
            <Progress value={stats.matchRate} className="w-full" />
          </div>
        </CardContent>
      </Card>

      {/* Financial Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg text-green-600">Entradas (Créditos)</CardTitle>
            <CardDescription>Total de recebimentos no período</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-green-600">
              {formatCurrency(stats.totalCredit)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg text-red-600">Saídas (Débitos)</CardTitle>
            <CardDescription>Total de pagamentos no período</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-red-600">
              {formatCurrency(stats.totalDebit)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Transações Recentes</CardTitle>
              <CardDescription>Mostrando as últimas 10 transações</CardDescription>
            </div>
            <Button variant="outline" size="sm">
              <Eye className="h-4 w-4 mr-2" />
              Ver Todas
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {transactions.slice(0, 10).map((transaction) => (
              <div
                key={transaction.id}
                className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div>
                    <p className="font-medium">{transaction.description}</p>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(transaction.transaction_date)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <p className={`font-semibold ${
                    transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {transaction.amount > 0 ? '+' : ''}{formatCurrency(transaction.amount)}
                  </p>

                  <Badge
                    variant="outline"
                    className={getStatusColor(transaction.match_status)}
                  >
                    {getStatusText(transaction.match_status)}
                  </Badge>
                </div>
              </div>
            ))}

            {transactions.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                Nenhuma transação encontrada
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Action Alert */}
      {stats.unmatched > 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <span className="font-medium">{stats.unmatched} transações</span> ainda não foram pareadas.
            Clique em "Conciliar Automaticamente" para tentar parear ou revise manualmente.
          </AlertDescription>
        </Alert>
      )}

      {/* Completion Alert */}
      {stats.matched === stats.total && stats.total > 0 && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            <span className="font-medium">Conciliação concluída!</span> Todas as transações foram pareadas com sucesso.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}